import { c as _c } from "react-compiler-runtime";
import styled from 'foundations-theming/utils';
import { fwcColor } from 'foundations-theming/utils/fwc';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { CELL_ACTION_MENU_ATTRIBUTE } from '../../table/constants/cellSelectionAttributes';

/**
 * A single segment of the floating cell-selection action menu.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Pill = styled.div.withConfig({
  displayName: "CellActionMenu__Pill"
})(["display:inline-flex;align-items:stretch;overflow:hidden;border-radius:", ";background-color:", ";color:", ";box-shadow:", ";"], ({
  theme
}) => theme.borderRadius.full, ({
  theme
}) => theme.color[fwcColor('fwc-button-fill-primary-default')], ({
  theme
}) => theme.color[fwcColor('fwc-button-text-primary-default')], ({
  theme
}) => theme.boxShadow['300']);
const Content = styled.span.withConfig({
  displayName: "CellActionMenu__Content"
})(["display:inline-flex;align-items:center;gap:", ";"], ({
  theme
}) => theme.space['200']);
const Label = styled.span.withConfig({
  displayName: "CellActionMenu__Label"
})(["white-space:nowrap;font-family:", ";font-size:", ";font-weight:", ";line-height:", ";"], ({
  theme
}) => theme.typography['detail-100'].fontFamily, ({
  theme
}) => theme.typography['detail-100'].fontSize, ({
  theme
}) => theme.typography['detail-100'].fontWeight, ({
  theme
}) => theme.typography['detail-100'].lineHeight);
const Segment = styled.button.withConfig({
  displayName: "CellActionMenu__Segment"
})(["display:inline-flex;align-items:center;margin:0;border:0;background:transparent;color:inherit;cursor:pointer;padding-block:", ";padding-inline:", ";font-size:", ";transition:background-color 0.1s ease-in-out;&:hover:not([aria-disabled='true']){background-color:", ";}&:active:not([aria-disabled='true']){background-color:", ";}&:focus-visible{outline:", " solid ", ";outline-offset:-4px;}&[aria-disabled='true']{cursor:default;}&[aria-disabled='true'] ", "{opacity:0.5;}"], ({
  theme
}) => theme.space['200'], ({
  theme
}) => theme.space['400'], ({
  theme
}) => theme.typography['detail-100'].fontSize, ({
  theme
}) => theme.color[fwcColor('fwc-button-fill-primary-hover')], ({
  theme
}) => theme.color[fwcColor('fwc-button-fill-primary-pressed')], ({
  theme
}) => theme.borderWidth['200'], ({
  theme
}) => theme.color[fwcColor('fwc-button-text-primary-default')], Content);

/**
 * The floating action menu shown next to the active selection's fill handle. It
 * renders as a Trellis-"primary" split button: a single rounded pill divided
 * into per-action segments (icon + label).
 *
 * Trellis has no split-button / button-group component yet (the alpha one isn't
 * cleared for use), so this is a hand-styled stand-in built from primary-button
 * tokens. The `items` prop is intentionally shaped like a button group so a real
 * Trellis component can replace this internals-only file later without changing
 * callers.
 */
export const CellActionMenu = t0 => {
  const $ = _c(6);
  const {
    ariaLabel,
    items
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      [CELL_ACTION_MENU_ATTRIBUTE]: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== items) {
    t2 = items.map(_temp);
    $[1] = items;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== ariaLabel || $[4] !== t2) {
    t3 = /*#__PURE__*/_jsx(Pill, Object.assign({
      role: "group",
      "aria-label": ariaLabel,
      "data-test-id": "cell-action-menu"
    }, t1, {
      children: t2
    }));
    $[3] = ariaLabel;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
function _temp(t0) {
  const {
    key,
    label,
    Icon,
    onClick,
    disabled,
    tooltip
  } = t0;
  const segment = /*#__PURE__*/_jsx(Segment, {
    type: "button",
    "aria-disabled": disabled || undefined,
    "data-test-id": `cell-action-menu-${key}`,
    onClick: e => {
      if (disabled) {
        return;
      }
      onClick();
      if (e.detail > 0) {
        e.currentTarget.blur();
      }
    },
    children: /*#__PURE__*/_jsxs(Content, {
      children: [/*#__PURE__*/_jsx(Icon, {}), /*#__PURE__*/_jsx(Label, {
        children: label
      })]
    })
  }, key);
  return tooltip ? /*#__PURE__*/_jsx(Tooltip, {
    title: tooltip,
    children: segment
  }, key) : segment;
}